// frontend/src/components/wizard/StepSource.jsx
import React, { useState, useEffect, useRef, useMemo } from 'react';

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  ServerOff, Loader2, ArrowRight, ArrowLeft,
  CheckCircle2, AlertTriangle, RefreshCw, Info,
  AlertCircle, ShieldCheck, Copy, Eye, EyeOff,
  Wrench, ChevronDown, ChevronUp, BarChart3
} from 'lucide-react';
import { generateDiagnosticRemediation } from '../../utils/dbaRequestGenerator';
import { useWizardStore } from '../../store/useWizardStore';
import { useLanguageStore } from '../../store/useLanguageStore';
import { apiClient } from '../../api/client';
import { EngineSelector, DatabaseForm, GoogleSheetForm, FlatFileForm } from './ConnectionComponents';
import { ProfileModal } from '../ProfileModal';
import { Modal } from '../Modal';

// ── Diagnostic Panel (mostrado dentro de errores de conexión) ────────────────
function DiagnosticPanel({ diagnostic, t }) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(diagnostic);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  return (
    <div className="mt-3 border-t border-red-200 pt-3">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-sm font-medium text-red-800 hover:text-red-900 transition-colors"
      >
        <Wrench className="w-4 h-4" />
        {t('dba_request.diag_title') || '¿Qué debe hacer tu DBA?'}
        {expanded
          ? <ChevronUp className="w-3.5 h-3.5" />
          : <ChevronDown className="w-3.5 h-3.5" />
        }
      </button>

      {expanded && (
        <div className="mt-2 animate-in fade-in">
          <pre className="bg-white border border-red-100 rounded-lg p-3 text-xs text-slate-800 font-mono whitespace-pre-wrap leading-relaxed overflow-x-auto">
            {diagnostic}
          </pre>
          <button
            type="button"
            onClick={handleCopy}
            className="mt-2 flex items-center gap-1.5 px-3 py-1.5 bg-white border border-red-200 hover:border-red-300 rounded-lg text-xs font-medium text-red-700 hover:bg-red-50 transition-all"
          >
            {copied ? (
              <>
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                <span className="text-emerald-700">{t('common.copied') || '¡Copiado!'}</span>
              </>
            ) : (
              <>
                <Copy className="w-3.5 h-3.5" />
                {t('dba_request.copy_for_dba') || 'Copiar para tu DBA'}
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

export default function StepSource() {
  const {
    source, target, setSource, setSourceColumns, setTargetColumns, setMapping, nextStep, migrationName, setMigrationName, editId,
    sourceSelectedTables, addSourceTable, removeSourceTable, reorderSourceTables
  } = useWizardStore();
  const { t, language } = useLanguageStore();

  // ── i18n: actualizar nombre de migración cuando cambia el idioma ──
  useEffect(() => {
    const defaultNames = ['Nueva Migración', 'New Migration', 'Nova Migração'];
    if (!migrationName || defaultNames.includes(migrationName)) {
      setMigrationName(t('wizard.new_migration') || 'Nueva Migración');
    }
  }, [language, t, migrationName, setMigrationName]);

  useEffect(() => {
    // Restaurar availableTables al regresar
    if (source?.config?.availableTables) {
      setAvailableTables(source.config.availableTables);
      setConnectionSuccess(true);
    }
  }, []);  // ⚠️ CAMBIAR: vacío [] en lugar de [source?.config?.availableTables]

  // 🆕 Restaurar selectedTable cuando carga (al regresar)
  useEffect(() => {
    const { source } = useWizardStore.getState();

    // Si hay tabla guardada en el store Y no hay tabla en estado local
    if (source?.config?.selectedTable && !selectedTable) {
      setSelectedTable(source.config.selectedTable);
    }
  }, []);  // ⚠️ SOLO al montar


  // 🆕 FUNCIÓN AUXILIAR para obtener supportedJoins
  const getSupportedJoins = async (engineType) => {
    try {
      // const res = await apiClient.get(`/onboarding/engines/${engineType}`);
      const res = await apiClient.get(`/catalogs/engines/${engineType}`);

      return res.data?.config?.supported_joins || ['INNER', 'LEFT', 'RIGHT', 'FULL'];
    } catch (e) {
      console.warn(`No se pudo obtener supported_joins para ${engineType}:`, e);
      return ['INNER', 'LEFT', 'RIGHT', 'FULL'];
    }
  }

  // Helper reutilizable
  const trimmedStr = z.string().optional().transform(v => v?.trim() ?? '');

  // ── Esquema Zod (depende de t para mensajes localizados) ──────────
  // La validación usa sec_engine.name exacto (DB canonical).
  // Los motores DB (SQL + NOSQL) requieren host, user, password, database.
  // Los motores FLAT/SAAS usan host como ruta o ID.
  const sourceSchema = useMemo(() => z.object({
    migrationName: z.string()
      .min(3, t('wizard.validation.name_req') || 'Ingresa nombre del pipeline (mín. 3 caract.)')
      .max(45, t('wizard.validation.name_max') || 'Máx. 45 caract.'),
    engineType: z.string(),
    host: trimmedStr,
    port: trimmedStr,
    user: trimmedStr,
    password: trimmedStr,
    database: trimmedStr,
    uploadMethod: trimmedStr,
    localFile: z.any().optional(),
    originalFileName: trimmedStr,
    // 🆕 SSH FIELDS
    useSsh: z.boolean().optional().default(false),
    ssh_host: trimmedStr,
    ssh_port: z.string().optional().default('22'),
    ssh_user: trimmedStr,
    ssh_pkey_content: trimmedStr,
    ssh_pkey_file: z.any().optional(),
    ssh_remote_bind_host: z.string().optional().default('localhost'),
    networkType: z.string().optional().default('cloud'),
    connectionMethod: z.enum(['ssh', 'firewall', 'direct']).optional().default('firewall'),
    warehouse: trimmedStr,
    schema: trimmedStr,
    auth_type: z.string().optional().default('none'),
    api_key_name: trimmedStr,
    api_key_location: z.string().optional().default('header'),
    token_url: trimmedStr,
    pagination_type: z.string().optional().default('auto'),
    param_offset: trimmedStr,
    param_limit: trimmedStr,
    param_page: trimmedStr,
    param_per_page: trimmedStr,
    cursor_field: trimmedStr,
    cursor_param: trimmedStr,
    page_size: trimmedStr,
    data_path: trimmedStr,
    custom_headers: trimmedStr,
    rootPath: z.string().optional().default(''),
  }).superRefine((data, ctx) => {
    // DB engines (SQL + NOSQL) → validar credenciales completas
    if (['mysql', 'postgres', 'oracle', 'mssql', 'mongodb', 'odoo', 'sap_hana', 'mariadb', 'azure_sql', 'salesforce', 'snowflake', 'redshift'].includes(data.engineType)) {
      if (!data.host) ctx.addIssue({ path: ['host'], message: t('wizard.validation.host_req'), code: 'custom' });
      if (!data.user) ctx.addIssue({ path: ['user'], message: t('wizard.validation.user_req'), code: 'custom' });
      if (!data.password) ctx.addIssue({ path: ['password'], message: t('wizard.validation.pass_req'), code: 'custom' });
      if (!['salesforce', 'hubspot'].includes(data.engineType) && !data.database) {
        ctx.addIssue({ path: ['database'], message: t('wizard.validation.db_req'), code: 'custom' });
      }
      if (data.engineType === 'snowflake' && !data.warehouse) {
        ctx.addIssue({ path: ['warehouse'], message: t('wizard.validation.warehouse_req') || 'Warehouse es requerido', code: 'custom' });
      }
    }
    // FLAT/SAAS → solo Google Sheets necesita ID obligatorio
    if (data.engineType === 'googlesheets' && !data.host) {
      ctx.addIssue({ path: ['host'], message: t('wizard.validation.id_req'), code: 'custom' });
    }

    // BigQuery → solo dataset requerido (auth via service account JSON en el servidor)
    if (data.engineType === 'bigquery' && !data.database) {
      ctx.addIssue({ path: ['database'], message: t('wizard.validation.dataset_req') || 'Dataset es requerido', code: 'custom' });
    }

    // REST API → host (URL Base) + database (Endpoint) requeridos + auth-specific
    if (data.engineType === 'rest_api') {
      if (!data.host) ctx.addIssue({ path: ['host'], message: t('wizard.validation.base_url_req') || 'URL Base es requerida', code: 'custom' });
      if (!data.database) ctx.addIssue({ path: ['database'], message: t('wizard.validation.endpoint_req') || 'Endpoint es requerido', code: 'custom' });
      if (data.auth_type === 'api_key' && !data.password) {
        ctx.addIssue({ path: ['password'], message: t('wizard.validation.api_key_req') || 'API Key es requerido', code: 'custom' });
      }
      if (data.auth_type === 'bearer' && !data.password) {
        ctx.addIssue({ path: ['password'], message: t('wizard.validation.token_req') || 'Token es requerido', code: 'custom' });
      }
      if (data.auth_type === 'basic') {
        if (!data.user) ctx.addIssue({ path: ['user'], message: t('wizard.validation.user_req'), code: 'custom' });
        if (!data.password) ctx.addIssue({ path: ['password'], message: t('wizard.validation.pass_req'), code: 'custom' });
      }
      if (data.auth_type === 'oauth2_cc') {
        if (!data.user) ctx.addIssue({ path: ['user'], message: 'Client ID es requerido', code: 'custom' });
        if (!data.password) ctx.addIssue({ path: ['password'], message: 'Client Secret es requerido', code: 'custom' });
        if (!data.token_url) ctx.addIssue({ path: ['token_url'], message: 'Token URL es requerida', code: 'custom' });
      }
    }

    // HubSpot → solo Access Token (password) requerido
    if (data.engineType === 'hubspot') {
      if (!data.password) ctx.addIssue({ path: ['password'], message: t('wizard.validation.token_req') || 'Access Token es requerido', code: 'custom' });
    }

    // Dynamics 365 → host (Org URL), user (Client ID), password (Client Secret), database (Tenant ID)
    if (data.engineType === 'dynamics365') {
      if (!data.host) ctx.addIssue({ path: ['host'], message: t('wizard.validation.org_url_req') || 'URL de la organización es requerida', code: 'custom' });
      if (!data.user) ctx.addIssue({ path: ['user'], message: t('wizard.validation.client_id_req') || 'Client ID es requerido', code: 'custom' });
      if (!data.password) ctx.addIssue({ path: ['password'], message: t('wizard.validation.client_secret_req') || 'Client Secret es requerido', code: 'custom' });
      if (!data.database) ctx.addIssue({ path: ['database'], message: t('wizard.validation.tenant_id_req') || 'Tenant ID es requerido', code: 'custom' });
    }

    // Zoho CRM → host (Accounts URL), user (Client ID), password (Client Secret), database (Refresh Token)
    if (data.engineType === 'zoho_crm') {
      if (!data.host) ctx.addIssue({ path: ['host'], message: t('wizard.validation.zoho_domain_req') || 'Zoho Domain es requerido', code: 'custom' });
      if (!data.user) ctx.addIssue({ path: ['user'], message: t('wizard.validation.client_id_req') || 'Client ID es requerido', code: 'custom' });
      if (!data.password) ctx.addIssue({ path: ['password'], message: t('wizard.validation.client_secret_req') || 'Client Secret es requerido', code: 'custom' });
      if (!data.database) ctx.addIssue({ path: ['database'], message: t('wizard.validation.refresh_token_req') || 'Refresh Token es requerido', code: 'custom' });
    }

    // Zoho Books → mismos campos que Zoho CRM (OAuth 2.0 Self Client)
    if (data.engineType === 'zoho_books') {
      if (!data.host) ctx.addIssue({ path: ['host'], message: t('wizard.validation.zoho_domain_req') || 'Zoho Domain es requerido', code: 'custom' });
      if (!data.user) ctx.addIssue({ path: ['user'], message: t('wizard.validation.client_id_req') || 'Client ID es requerido', code: 'custom' });
      if (!data.password) ctx.addIssue({ path: ['password'], message: t('wizard.validation.client_secret_req') || 'Client Secret es requerido', code: 'custom' });
      if (!data.database) ctx.addIssue({ path: ['database'], message: t('wizard.validation.refresh_token_req') || 'Refresh Token es requerido', code: 'custom' });
    }

    // Zoho Inventory → mismos campos que Zoho CRM (OAuth 2.0 Self Client)
    if (data.engineType === 'zoho_inventory') {
      if (!data.host) ctx.addIssue({ path: ['host'], message: t('wizard.validation.zoho_domain_req') || 'Zoho Domain es requerido', code: 'custom' });
      if (!data.user) ctx.addIssue({ path: ['user'], message: t('wizard.validation.client_id_req') || 'Client ID es requerido', code: 'custom' });
      if (!data.password) ctx.addIssue({ path: ['password'], message: t('wizard.validation.client_secret_req') || 'Client Secret es requerido', code: 'custom' });
      if (!data.database) ctx.addIssue({ path: ['database'], message: t('wizard.validation.refresh_token_req') || 'Refresh Token es requerido', code: 'custom' });
    }

    // SugarCRM → host (Instance URL), user (Username), password (Password), database (Client ID), port (Client Secret)
    if (data.engineType === 'sugarcrm') {
      if (!data.host) ctx.addIssue({ path: ['host'], message: t('wizard.validation.instance_url_req') || 'Instance URL es requerida', code: 'custom' });
      if (!data.user) ctx.addIssue({ path: ['user'], message: t('wizard.validation.user_req'), code: 'custom' });
      if (!data.password) ctx.addIssue({ path: ['password'], message: t('wizard.validation.pass_req'), code: 'custom' });
      if (!data.database) ctx.addIssue({ path: ['database'], message: t('wizard.validation.client_id_req') || 'Client ID es requerido', code: 'custom' });
      if (!data.port) ctx.addIssue({ path: ['port'], message: t('wizard.validation.client_secret_req') || 'Client Secret es requerido', code: 'custom' });
    }

    // NetSuite → host (Account ID), user (Consumer Key), password (Consumer Secret), database (Token Key), port (Token Secret)
    if (data.engineType === 'netsuite') {
      if (!data.host) ctx.addIssue({ path: ['host'], message: t('wizard.validation.account_id_req') || 'Account ID es requerido', code: 'custom' });
      if (!data.user) ctx.addIssue({ path: ['user'], message: t('wizard.validation.consumer_key_req') || 'Consumer Key es requerida', code: 'custom' });
      if (!data.password) ctx.addIssue({ path: ['password'], message: t('wizard.validation.consumer_secret_req') || 'Consumer Secret es requerido', code: 'custom' });
      if (!data.database) ctx.addIssue({ path: ['database'], message: t('wizard.validation.token_key_req') || 'Token Key es requerido', code: 'custom' });
      if (!data.port) ctx.addIssue({ path: ['port'], message: t('wizard.validation.token_secret_req') || 'Token Secret es requerido', code: 'custom' });
    }

    // SuiteCRM → host (Instance URL), user (Username), password (Password), database (Client ID), port (Client Secret)
    if (data.engineType === 'suitecrm') {
      if (!data.host) ctx.addIssue({ path: ['host'], message: t('wizard.validation.instance_url_req') || 'Instance URL es requerida', code: 'custom' });
      if (!data.user) ctx.addIssue({ path: ['user'], message: t('wizard.validation.user_req'), code: 'custom' });
      if (!data.password) ctx.addIssue({ path: ['password'], message: t('wizard.validation.pass_req'), code: 'custom' });
      if (!data.database) ctx.addIssue({ path: ['database'], message: t('wizard.validation.client_id_req') || 'Client ID es requerido', code: 'custom' });
      if (!data.port) ctx.addIssue({ path: ['port'], message: t('wizard.validation.client_secret_req') || 'Client Secret es requerido', code: 'custom' });
    }

    // Defontana → host (Ambiente), user (User/Email), password (Password), database (Company), port (Client code)
    if (data.engineType === 'defontana') {
      if (!data.user) ctx.addIssue({ path: ['user'], message: t('wizard.validation.user_req'), code: 'custom' });
      if (!data.password) ctx.addIssue({ path: ['password'], message: t('wizard.validation.pass_req'), code: 'custom' });
      if (!data.database) ctx.addIssue({ path: ['database'], message: t('wizard.validation.company_code_req') || 'Código de empresa es requerido', code: 'custom' });
      if (!data.port) ctx.addIssue({ path: ['port'], message: t('wizard.validation.client_code_req') || 'Código de cliente es requerido', code: 'custom' });
    }

    // 🆕 SSH VALIDATION: Si el usuario selecciona SSH, los campos SSH son OBLIGATORIOS
    if (data.connectionMethod === 'ssh' && data.networkType === 'local') {
      if (!data.ssh_host) {
        ctx.addIssue({
          path: ['ssh_host'],
          message: t('wizard.validation.ssh_host_req') || 'SSH Host es requerido',
          code: 'custom'
        });
      }
      if (!data.ssh_user) {
        ctx.addIssue({
          path: ['ssh_user'],
          message: t('wizard.validation.ssh_user_req') || 'SSH User es requerido',
          code: 'custom'
        });
      }
      if (!data.ssh_pkey_content && !data.ssh_pkey_file) {
        ctx.addIssue({
          path: ['ssh_pkey_file'],
          message: t('wizard.validation.ssh_key_req') || 'SSH Private Key es requerida',
          code: 'custom'
        });
      }
    }
  }), [t]);

  // ── Estado ────────────────────────────────────────────────────────
  const [isTesting, setIsTesting] = useState(false);
  // const [testError, setTestError] = useState(null);
  const [testError, setTestError] = useState(() => {
    const pwd = source?.config?.password || '';
    const isFlat = ['csv', 'excel', 'dbf', 'json'].includes(source?.type);
    if (editId && isFlat && pwd.startsWith('/app/')) {
      return {
        title: 'wizard.errors.ephemeral_file_title',  // se resuelve abajo
        description: 'wizard.errors.ephemeral_zdr_edit',    // se resuelve abajo
        isWarning: true,   // ← para el estilo ámbar, no rojo
      };
    }
    return null;
  });
  const [categories, setCategories] = useState([]);
  const [isLoadingCatalogs, setIsLoadingCatalogs] = useState(true);
  const [connectionSuccess, setConnectionSuccess] = useState(() => {
    if (editId && (source?.config?.selectedTable || sourceSelectedTables.length > 0)) return true;
    if (['csv', 'excel', 'googlesheets', 'dbf', 'json'].includes(source?.type))
      return !!(source?.config?.host || source?.config?.password || source?.config?.originalFileName);
    return Array.isArray(source?.config?.availableTables) && source.config.availableTables.length > 0;
  });
  const [availableTables, setAvailableTables] = useState(() => {
    const stored = source?.config?.availableTables;
    if (Array.isArray(stored) && stored.length > 0) return stored;
    // 🔧 Edit mode: semillar con tablas configuradas
    if (editId && sourceSelectedTables.length > 0) return sourceSelectedTables;
    if (editId && source?.config?.selectedTable) return [source.config.selectedTable];
    return [];
  });
  // const [selectedTable, setSelectedTable] = useState('');  // 🆕 Tabla actual siendo seleccionada (antes de agregar)
  // const [selectedTable, setSelectedTable] = useState(source?.config?.selectedTable || '');
  const [selectedTable, setSelectedTable] = useState(() => {
    // 🆕 Inicializar desde el store si existe
    return useWizardStore.getState().source?.config?.selectedTable || '';
  });
  console.log('🔵 STEPSOURCE RENDER INICIAL:', { selectedTable: source?.config?.selectedTable, availableTables: source?.config?.availableTables?.length || 0 });
  const [searchEngine, setSearchEngine] = useState('');
  const [expandedCats, setExpandedCats] = useState(['databases', 'files']);
  const [showPassword, setShowPassword] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [topValuesInfo, setTopValuesInfo] = useState(false);
  const [networkType, setNetworkType] = useState('cloud');

  const prevConnectionSuccessRef = useRef(false);


  // ── React Hook Form ───────────────────────────────────────────────
  const tempDir = import.meta.env.VITE_TEMP_FILE_DIR || '/app/exports/';
  const { register, handleSubmit, watch, setValue, getValues, formState: { errors } } = useForm({
    resolver: zodResolver(sourceSchema),
    defaultValues: {
      migrationName: migrationName,
      engineType: source?.type || 'mysql',
      host: source?.config?.host || '',
      port: source?.config?.port || '',
      user: source?.config?.user || '',
      password: source?.config?.password || '',
      database: source?.config?.database || '',
      uploadMethod: source?.config?.password?.startsWith(tempDir) ? 'local' : 'url',
      originalFileName: source?.config?.originalFileName || '',
      // 🆕 SSH FIELDS
      useSsh: source?.config?.useSsh || false,
      ssh_host: source?.config?.ssh_host || '',
      ssh_port: source?.config?.ssh_port || '22',
      ssh_user: source?.config?.ssh_user || '',
      ssh_pkey_content: source?.config?.ssh_pkey_content || '',
      ssh_remote_bind_host: source?.config?.ssh_remote_bind_host || 'localhost',
      networkType: 'cloud',
      connectionMethod: source?.config?.connectionMethod || 'firewall',
    },
  });

  // 🔵 DEBUG: useEffect para Restaurar tabla y validación al cargar/regresar
  useEffect(() => {
    const { source } = useWizardStore.getState();

    console.log('📍 useEffect RESTAURAR - Validando tabla en Source');
    console.log('   source.type:', source?.type);
    console.log('   source.config.selectedTable:', source?.config?.selectedTable);

    // 🆕 RESTAURAR EL MOTOR PRIMERO (para que se muestre el select)
    if (source?.type) {
      console.log('✅ Restaurando engineType:', source.type);
      setValue('engineType', source.type);  // ← ESTO ES CRÍTICO
    }

    // Luego restaurar tabla
    if (source?.config?.selectedTable && source.config.selectedTable !== '') {
      // if (source?.config?.selectedTable) {
      console.log('✅ Restaurando tabla:', source.config.selectedTable);
      setSelectedTable(source.config.selectedTable);
      setAvailableTables(source?.config?.availableTables || []);
      setConnectionSuccess(true);
    }
  }, [source?.config?.selectedTable]);

  // }, []);
  // }, [setValue]);  // ← CAMBIAR: Agregar setValue como dependencia


  const selectedEngine = watch('engineType');

  // ── Derivar type_motor desde el catálogo (evita listas hardcoded) ─
  // Cuando los catálogos carguen, sabemos el tipo de cada motor.
  // type_motor: "SQL" | "NOSQL" | "FLAT" | "NONE"
  const selectedEngineData = useMemo(
    () => categories.flatMap(c => c.options).find(o => o.engine_key === selectedEngine),
    [categories, selectedEngine]
  );
  const selectedTypeMotor = selectedEngineData?.type_motor; // undefined hasta que carguen catálogos

  // ── Cargar catálogo de motores ────────────────────────────────────
  useEffect(() => {
    apiClient.get('/catalogs/engines?type=source')
      .then(res => { setCategories(res.data); setIsLoadingCatalogs(false); });
  }, []);

  // 🆕 Al cargar en edición, restaurar tablas seleccionadas
  useEffect(() => {
    if (sourceSelectedTables.length > 0 && !selectedTable) {
      // Si hay tablas cargadas desde API (edición), mostrar la lista
      setAvailableTables(source?.config?.availableTables || []);
    }
  }, [sourceSelectedTables, source?.config?.availableTables]);

  // ── Resetear formulario al cambiar de motor ───────────────────────
  const prevEngineRef = useRef(source?.type || 'mysql');
  useEffect(() => {
    if (isLoadingCatalogs || categories.length === 0) return;

    const foundConfig = selectedEngineData?.config_default || {};

    // Primera carga: rellenar puerto si está vacío
    const currentPort = getValues('port');
    // if (!currentPort && foundConfig.default_port) {
    //   setValue('port', foundConfig.default_port, { shouldDirty: false });
    // }
    // 🔧 mismo guard que StepTarget: no auto-completar port en edit mode ni conexión existente
    const hasExistingConnection =
      (source?.config?.availableTables?.length ?? 0) > 0 ||
      Boolean(editId && (source?.config?.selectedTable || sourceSelectedTables.length > 0));
    if (!currentPort && foundConfig.default_port && !hasExistingConnection) {
      setValue('port', foundConfig.default_port, { shouldDirty: false });
    }

    // Cambio de motor: resetear todo
    if (selectedEngine !== prevEngineRef.current) {
      prevEngineRef.current = selectedEngine;
      setConnectionSuccess(false);
      setTestError(null);

      // Limpiar credenciales estándar
      setValue('host', '');
      setValue('database', '');
      setValue('password', '');
      setValue('user', '');
      setValue('port', foundConfig.default_port || '');
      setSelectedTable('');
      setAvailableTables([]);

      // 🆕 CRÍTICO: Limpiar tabla del STORE para 1:1
      const sourceState = useWizardStore.getState();
      sourceState.setSource({
        type: sourceState.source.type,
        config: {
          ...sourceState.source.config,
          selectedTable: ''  // ✅ BORRAR
        }
      });

      // Limpiar tablas múltiples (N:M)
      sourceState.clearSelectedTables('source');
      // useWizardStore.getState().clearSelectedTables('source');


      // 🆕 Limpiar SSH
      setValue('useSsh', false);
      setValue('ssh_host', '');
      setValue('ssh_port', '22');
      setValue('ssh_user', '');
      setValue('ssh_pkey_content', '');
      setValue('ssh_pkey_file', null);
      setValue('ssh_remote_bind_host', 'localhost');

      // 🆕 Limpiar networkType y connectionMethod
      setValue('networkType', 'cloud');
      setValue('connectionMethod', 'firewall');
    }
  }, [selectedEngine, categories, isLoadingCatalogs, selectedEngineData, setValue, getValues]);

  // ── Invalidar conexión al editar campos ───────────────────────────
  useEffect(() => {
    const sub = watch((_, { name, type }) => {
      if (type === 'change' &&
        ['host', 'port', 'user', 'password', 'database', 'localFile', 'engineType',
          'ssh_host', 'ssh_port', 'ssh_user', 'ssh_pkey_content', 'ssh_remote_bind_host'].includes(name)) {
        setConnectionSuccess(false);
        setTestError(null);
      }
    });
    return () => sub.unsubscribe();
  }, [watch]);

  // 🆕 Sincronizar form watch con estado local
  useEffect(() => {
    const formNetworkType = watch('networkType');
    if (formNetworkType !== networkType) {
      setNetworkType(formNetworkType);
    }
  }, [watch('networkType'), networkType, watch]);

  const useSshValue = watch('useSsh');
  const connectionMethodValue = watch('connectionMethod');
  const hostValue = watch('host');
  const portValue = watch('port');
  const userValue = watch('user');
  const passwordValue = watch('password');
  const uploadMethodValue = watch('uploadMethod');
  const fileValue = watch('localFile');  // ← Cambiar a 'localFile'

  // ═══════════════════════════════════════════════════════════════════════════════
  // 🆕 useEffect que resetea cuando cambian estos valores
  // ═══════════════════════════════════════════════════════════════════════════════
  // 🔧 En edit mode, tratar la conexión como establecida aunque no haya availableTables
  // guardado (pipelines creados antes del fix de cleanConfig).
  // Esto evita que el reset path del connectionSuccess effect sea alcanzable.
  const hasStoredAvailableTables =
    (source?.config?.availableTables && Array.isArray(source.config.availableTables) && source.config.availableTables.length > 0) ||
    Boolean(editId && (source?.config?.selectedTable || sourceSelectedTables.length > 0));


  useEffect(() => {
    // Solo resetear si ya hay una conexión validada (no molestamos mientras escribe)
    if (!connectionSuccess) {
      prevConnectionSuccessRef.current = false;
      return;
    }

    // 🆕 SI vino del store (availableTables), NO resetear aunque cambien los campos
    if (hasStoredAvailableTables) {
      return;  // ← NUEVO: ignora cambios en fields si viene del store
    }

    // Si ACABAMOS DE VALIDAR en este render (false → true), no resetear
    if (!prevConnectionSuccessRef.current && connectionSuccess) {
      prevConnectionSuccessRef.current = true;
      return;
    }

    // Resetear cuando cambian los parámetros de conexión
    setTestError(null);
    setConnectionSuccess(false);
    setSelectedTable('');
    setAvailableTables([]);

  }, [
    useSshValue, connectionMethodValue, networkType, hostValue, portValue,
    userValue, passwordValue, uploadMethodValue, fileValue,
    connectionSuccess, hasStoredAvailableTables  // ← Agregar esta dependencia
  ]);

  // useEffect(() => {
  //   if (connectionSuccess) {
  //     justValidatedRef.current = true;
  //   }
  // }, [connectionSuccess]);

  // 🆕 Al cargar, restaurar tablas seleccionadas si vienen del store
  useEffect(() => {
    const { sourceSelectedTables, source } = useWizardStore.getState();

    // Si hay tablas múltiples guardadas en el store, mostrarlas
    if (sourceSelectedTables.length > 0) {
      setAvailableTables(source?.config?.availableTables || []);
      setConnectionSuccess(true);  // Marcar como validada
    }
  }, []);

  // Resolver claves i18n del aviso ZDR si fue inicializado antes de que t() existiera
  useEffect(() => {
    if (testError?.title === 'wizard.errors.ephemeral_file_title') {
      setTestError({
        title: t('wizard.errors.ephemeral_file_title') || 'Archivo no disponible',
        description: t('wizard.errors.ephemeral_zdr_edit') ||
          'Este archivo fue eliminado de nuestros servidores tras la ejecución anterior ' +
          '(política Zero Data Retention). Puedes continuar editando el pipeline, ' +
          'pero deberás subir el archivo nuevamente antes de volver a ejecutarlo.',
        isWarning: true,
      });
    }
  }, [t]);  // corre cuando t() ya está disponible

  // ── Parsear Google Sheets URL → ID ───────────────────────────────
  const handleHostChange = (e) => {
    let val = e.target.value;
    if (selectedEngine === 'googlesheets') {
      const match = val.match(/\/d\/([a-zA-Z0-9-_]+)/);
      if (match?.[1]) val = match[1];
    }
    setValue('host', val, { shouldValidate: true });
    setConnectionSuccess(false);
    setTestError(null);
  };

  // ── Submit: subir archivo local si aplica + test-connection ───────
  const onSubmit = async (data) => {
    // 🔧 Trim todos los campos string — elimina espacios copiados del portapapeles
    Object.keys(data).forEach(k => {
      if (typeof data[k] === 'string') data[k] = data[k].trim();
    });
    setIsTesting(true);
    setTestError(null);

    try {
      setMigrationName(data.migrationName);

      let finalPassword = data.password;
      let finalOriginalFileName = data.originalFileName;

      // Upload de archivo local (CSV/Excel/DBF) al servidor temporal
      if (['csv', 'excel', 'dbf', 'json'].includes(data.engineType) &&
        data.uploadMethod === 'local' &&
        data.localFile?.[0]) {
        finalOriginalFileName = data.localFile[0].name;
        setValue('originalFileName', finalOriginalFileName);

        if (!finalPassword || !finalPassword.startsWith(tempDir)) {
          const formData = new FormData();
          formData.append('file', data.localFile[0]);
          // const uploadRes = await apiClient.post('/onboarding/upload-temp', formData);
          const uploadRes = await apiClient.post('/onboarding/upload-temp', formData, {
            headers: { 'Content-Type': undefined },
          });
          finalPassword = uploadRes.data.filePath;
          setValue('password', finalPassword);
        }
      }

      const { localFile, ...configToSave } = data;
      // 🆕 Si viene archivo SSH, leerlo como string
      if (data.ssh_pkey_file) {
        // ssh_pkey_file puede ser: File, FileList, o array [File]
        const fileObj = Array.isArray(data.ssh_pkey_file)
          ? data.ssh_pkey_file[0]  // Si es array
          : data.ssh_pkey_file?.[0] || data.ssh_pkey_file;  // Si es FileList o File directo

        if (fileObj && fileObj instanceof File) {
          const sshKeyText = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => resolve(e.target.result);
            reader.onerror = reject;
            reader.readAsText(fileObj);  // ✅ AHORA SÍ ES FILE
          });
          configToSave.ssh_pkey_content = sshKeyText;
          configToSave.useSsh = true;
        }
      }
      configToSave.password = finalPassword;
      configToSave.originalFileName = finalOriginalFileName;
      configToSave.connectionMethod = data.connectionMethod;  // 🆕 Guardar el método elegido

      // 🆕 JSON: guardar rootPath en config para que DataReader lo use
      if (data.engineType === 'json' && data.rootPath?.trim()) {
        configToSave.rootPath = data.rootPath.trim();
      }

      // 🆕 LIMPIAR CAMPOS SSH SI ES NUBE PÚBLICA O NO USA SSH
      if (data.networkType === 'cloud' || (data.networkType === 'local' && data.connectionMethod !== 'ssh')) {
        configToSave.ssh_host = '';
        configToSave.ssh_port = '';
        configToSave.ssh_user = '';
        configToSave.ssh_pkey_content = '';
        configToSave.ssh_remote_bind_host = '';
        configToSave.useSsh = false;
      }
      const tableToUse = selectedTable || source?.config?.selectedTable;
      // FLAT/SAAS: no necesitan test-connection — conexión inmediata
      if (selectedTypeMotor === 'FLAT' || selectedTypeMotor === 'NONE') {
        const isSheet = ['excel', 'googlesheets'].includes(data.engineType);
        const fakeTable = isSheet
          ? 'Hoja 1'
          : (data.uploadMethod === 'local' ? finalOriginalFileName : 'Datos del Archivo');
        const finalTable = tableToUse || fakeTable;

        // ── Verificar hoja en Excel antes de aprobar ─────────────────
        // CSV/DBF no tienen hojas. GoogleSheets ya lo verifica via WorksheetNotFound.
        if (data.engineType === 'excel' && finalTable && finalTable !== 'Hoja 1') {
          try {
            const colRes = await apiClient.post('/onboarding/columns', {
              type: data.engineType,
              config: {
                ...configToSave,
                selectedTable: finalTable,
                ...(editId ? { _idConfigHead: editId, _side: 'source' } : {}),
              },
            });
            if (!colRes.data.success || !colRes.data.columns?.length) {
              throw new Error('empty');
            }
            // Columnas leídas correctamente — actualizar sourceColumns
            setSourceColumns(colRes.data.columns);
          } catch (e) {
            const detail = e.response?.data?.detail;
            setTestError({
              title: t('wizard.errors.sheet_not_found_title') || 'Hoja no encontrada',
              description: detail?.message
                || `${t('wizard.errors.sheet_not_found_desc') || 'La hoja'} "${finalTable}" ${t('wizard.errors.sheet_not_found_suffix') || 'no existe en el archivo. Verifica el nombre exacto.'}`,
            });
            setIsTesting(false);
            return;
          }
        }

        // Garantizar que selectedTable esté siempre en availableTables
        const baseList = availableTables.filter(Boolean).length > 0 ? availableTables : [fakeTable];
        const finalList = baseList.includes(finalTable)
          ? baseList
          : [...baseList, finalTable].filter(Boolean);

        const supportedJoins = await getSupportedJoins(data.engineType);

        setSource({
          type: data.engineType,
          config: { ...configToSave, availableTables: finalList, selectedTable: finalTable },
          supportedJoins
        });
        setAvailableTables(finalList);
        setSelectedTable(finalTable);
        setConnectionSuccess(true);
        return;
      }

      // DB (SQL / NOSQL): test-connection real al backend
      const res = await apiClient.post('/onboarding/test-connection', {
        engine: data.engineType,
        ...configToSave,
        // 🔒 Sprint 12: credential masking — enviar pipeline ID en edit mode
        ...(editId ? { idConfigHead: editId, side: 'source' } : {}),
      });

      if (res.data.success) {
        const supportedJoins = await getSupportedJoins(data.engineType);

        setSource({
          type: data.engineType,
          config: {
            ...configToSave,
            availableTables: res.data.tables,
            selectedTable: tableToUse
          },
          supportedJoins
        });

        // setSource({ type: data.engineType, config: { ...configToSave, availableTables: res.data.tables, selectedTable } });
        if (res.data.columns) setSourceColumns(res.data.columns);
        setAvailableTables(res.data.tables || []);
        setConnectionSuccess(true);
      } else {
        // 🆕 Backend retornó error (success: false)
        const diagnostic = res.data.error_diagnostic || null;
        let diagnosticText = '';
        if (diagnostic) {
          const formData = watch();
          diagnosticText = generateDiagnosticRemediation({
            diagnosticCode: diagnostic,
            engineType: formData.engineType,
            host: formData.host,
            port: formData.port,
            database: formData.database,
            user: formData.user,
            connectionMethod: formData.connectionMethod,
            sshHost: formData.ssh_host,
            t,
          });
        }
        setTestError({
          title: res.data.error_type === 'ssh_validation' ? 'Validación SSH' : t('wizard.errors.conn_failed'),
          description: t(`errors.${res.data.error}`, res.data.error_params || {}) !== `errors.${res.data.error}` ? t(`errors.${res.data.error}`, res.data.error_params || {}) : (res.data.error || t('wizard.errors.unknown')),
          diagnostic: diagnosticText,
          diagnosticCode: diagnostic,
        });
      }

    } catch (err) {
      setTestError({
        title: t('wizard.errors.conn_failed'),
        description: err.message,
      });
    } finally {
      setIsTesting(false);
    }
  };

  // ── Continuar: forzar lectura de columnas antes de avanzar ────────
  const handleContinue = async () => {
    setIsTesting(true);
    const data = watch();
    const { localFile, ...cleanData } = data;

    // 🆕 SSH: leer archivo .pem como texto (mismo patrón que handleTestConnection)
    if (data.ssh_pkey_file && data.connectionMethod === 'ssh' && data.networkType === 'local') {
      const fileObj = Array.isArray(data.ssh_pkey_file)
        ? data.ssh_pkey_file[0]
        : data.ssh_pkey_file?.[0] || data.ssh_pkey_file;

      if (fileObj && fileObj instanceof File) {
        const sshKeyText = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target.result);
          reader.onerror = reject;
          reader.readAsText(fileObj);
        });
        cleanData.ssh_pkey_content = sshKeyText;
        cleanData.useSsh = true;
      }
    }

    // ✅ FIX DEFINITIVO: getState() lee el store en tiempo real,
    // evita el stale closure de 'source' del render anterior
    if (['csv', 'excel', 'dbf', 'json'].includes(cleanData.engineType)) {
      const storePassword = useWizardStore.getState().source?.config?.password;
      cleanData.password = cleanData.password || storePassword || '';

      // Archivo efímero ZDR: fue eliminado del servidor tras el ETL anterior.
      // Mostrar aviso claro, pero permitir continuar para editar mapping/schedule.
      // El pipeline NO se ejecutará hasta que el usuario re-suba el archivo.
      if (cleanData.password?.startsWith('/app/') && editId) {
        // setTestError({
        //   title: t('wizard.errors.ephemeral_file_title') || 'Archivo no disponible',
        //   description: t('wizard.errors.ephemeral_zdr_edit') ||
        //     'Este archivo fue eliminado de nuestros servidores tras la ejecución anterior ' +
        //     '(política Zero Data Retention). Puedes continuar editando el pipeline, ' +
        //     'pero deberás subir el archivo nuevamente antes de ejecutarlo.',
        // });
        cleanData._skipColumnsForEphemeral = true;
        // ← No hace return: el usuario puede seguir
      } else if (!cleanData.password) {
        setTestError({
          title: t('wizard.errors.upload_required_title') || 'Archivo requerido',
          description: t('wizard.errors.upload_missing') || 'Debes subir el archivo antes de continuar.',
        });
        setIsTesting(false);
        return;   // ← Este sí bloquea: no hay nada que continuar
      }
    }

    // 🆕 DETECTAR MODO: 1:1, 1:N, N:1, N:M
    const sourceCount = sourceSelectedTables.length || (selectedTable ? 1 : 0);
    const targetCount = useWizardStore.getState().targetSelectedTables.length ||
      (useWizardStore.getState().target?.config?.selectedTable ? 1 : 0);

    // Validar conteo SOLO si ambos tienen múltiples (N:M)
    if (sourceCount > 1 && targetCount > 1 && sourceCount !== targetCount) {
      setTestError({
        title: t('wizard.errors.table_count_mismatch') || 'Cantidad de tablas',
        description: t('wizard.errors.table_count_desc') ||
          `Se requieren ${sourceCount} tablas destino, pero tienes ${targetCount}.`,
      });
      setIsTesting(false);
      return;
    }

    // 🆕 Detectar si es multi-tabla (para evitar obtener columnas)
    const isMultiTable = sourceCount > 1;

    // 🆕 Solo obtener columnas en modo 1:1
    // En N:M, las columnas de cada tabla se obtienen en StepMapping
    // if (!isMultiTable) {
    if (!isMultiTable && !cleanData._skipColumnsForEphemeral) {
      try {
        const tableToFetch = ['csv', 'dbf', 'json'].includes(cleanData.engineType)
          ? 'Datos del Archivo'
          : selectedTable;

        const res = await apiClient.post('/onboarding/columns', {
          type: cleanData.engineType,
          config: {
            ...cleanData,
            selectedTable: tableToFetch,
            ...(editId ? { _idConfigHead: editId, _side: 'source' } : {}),
          },
        });

        if (res.data.success && res.data.columns) {
          setSourceColumns(res.data.columns);
        }
      } catch (e) {
        console.error('Error al obtener columnas de origen:', e);
        setIsTesting(false);
        const isEphemeral = e.response?.data?.detail?.code === 'ERR_EPHEMERAL_FILE';
        setTestError({
          title: isEphemeral
            ? (t('wizard.errors.ephemeral_file_title') || 'Archivo no disponible')
            : (t('wizard.errors.schema_error') || 'Error de Esquema'),
          description: isEphemeral
            ? (t('wizard.errors.ephemeral_file_desc') || 'Este archivo fue eliminado tras la ejecución anterior (ZDR). Sube el archivo nuevamente.')
            : (t('wizard.errors.schema_desc') || 'No se pudieron leer las columnas de esta tabla.'),
        });
        return;
      }
    }

    const supportedJoins = await getSupportedJoins(cleanData.engineType);

    setSource({
      type: cleanData.engineType,
      config: {
        ...cleanData,
        selectedTable,
        availableTables
      },
      supportedJoins
    });
    setMigrationName(cleanData.migrationName);
    setIsTesting(false);

    nextStep();
  };

  if (isLoadingCatalogs) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  const toggleCategory = (key) =>
    setExpandedCats(prev => prev.includes(key) ? prev.filter(c => c !== key) : [...prev, key]);

  // ── Qué formulario mostrar según type_motor ────────────────────────
  // type_motor viene del catálogo /engines → derivado del engine_registry.
  // Esto es dinámico: agregar un motor nuevo a sec_engine lo muestra aquí
  // automáticamente sin tocar este componente.
  const showDatabaseForm = selectedTypeMotor === 'SQL' || selectedTypeMotor === 'NOSQL' || selectedTypeMotor === 'API';
  const showGoogleForm = selectedEngine === 'googlesheets';  // FLAT especial
  const showFlatForm = selectedTypeMotor === 'FLAT' && !showGoogleForm;

  // ── Selector de tabla: BD muestra select, sheets muestra input ─────
  const showTableSelect = showDatabaseForm;
  const showSheetInput = ['excel', 'googlesheets'].includes(selectedEngine);

  return (
    <div className="w-full max-w-3xl mx-auto pb-12">
      <div className="mb-5">
        <h2 className="text-2xl font-bold text-slate-900">{t('wizard.step_source.title')}</h2>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5 animate-in fade-in slide-in-from-bottom-2">

        {/* Selector de Motor */}
        <EngineSelector
          categories={categories}
          searchEngine={searchEngine}
          setSearchEngine={setSearchEngine}
          expandedCats={expandedCats}
          toggleCategory={toggleCategory}
          register={register}
          selectedEngine={selectedEngine}
          setValue={setValue}
        />

        {/* Formulario según type_motor (dinámico desde catálogo) */}
        {showDatabaseForm && (
          <DatabaseForm
            register={register}
            errors={errors}
            watch={watch}
            setValue={setValue}
            onTrimField={(name, value) => setValue(name, value.trim(), { shouldValidate: false })}
            showPassword={showPassword}
            setShowPassword={setShowPassword}
            isTarget={false}
            networkType={networkType}
            isApiEngine={selectedTypeMotor === 'API'}
            onNetworkTypeChange={(type) => {
              setNetworkType(type);
              setValue('networkType', type);
            }}
            connectionMethod={watch('connectionMethod')}
            onConnectionMethodChange={(method) => {
              setValue('connectionMethod', method);
            }}
          />
        )}

        {showGoogleForm && (
          <GoogleSheetForm
            register={register}
            errors={errors}
            handleHostChange={handleHostChange}
            isTarget={false}
            onTrimField={(name, value) => setValue(name, value.trim(), { shouldValidate: false })}
          />
        )}

        {showFlatForm && (
          <FlatFileForm
            register={register}
            errors={errors}
            isTarget={false}
            engineType={selectedEngine}
            showPassword={showPassword}
            setShowPassword={setShowPassword}
            watch={watch}
            setValue={setValue}
          />
        )}

        {/* Nombre de hoja — solo Excel — visible ANTES de validar */}
        {selectedEngine === 'excel' && (
          <div className="p-4 bg-white border border-slate-200 rounded-xl shadow-sm">
            <label className="text-sm font-medium text-slate-700 block mb-1">
              {t('wizard.step_source.sheet_name')}
            </label>
            <input
              type="text"
              value={selectedTable}
              onChange={(e) => setSelectedTable(e.target.value)}
              placeholder={t('wizard.step_source.sheet_ph') || 'Ej: Hoja 1'}
              className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-white focus:ring-2 focus:ring-indigo-600 outline-none"
            />
            <p className="text-[11px] text-slate-400 mt-1.5">
              {t('wizard.step_source.sheet_desc') || 'Escribe el nombre exacto de la hoja a migrar. Se verificará al presionar Validar Origen.'}
            </p>
          </div>
        )}

        {/* Root Path — solo JSON */}
        {selectedEngine === 'json' && (
          <div className="p-4 bg-white border border-slate-200 rounded-xl shadow-sm">
            <label className="text-sm font-medium text-slate-700 block mb-1">
              {t('wizard.step_source.json_root_path')}
            </label>
            <input
              type="text"
              {...register('rootPath')}
              placeholder='data, records, results...'
              className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:ring-2 focus:ring-indigo-600 outline-none"
            />
            <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">
              {t('wizard.step_source.json_root_path_hint')}
            </p>
          </div>
        )}

        {/* Nombre de la migración */}
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
          <label className="text-sm font-bold text-slate-900 mb-2 block">
            {t('wizard.step_source.migration_name')}
          </label>
          <input
            {...register('migrationName')}
            placeholder={t('wizard.step_source.migration_name_ph')}
            className={`w-full px-4 py-2 rounded-lg border outline-none ${errors.migrationName ? 'border-red-500' : 'border-slate-200'
              }`}
          />
          {errors.migrationName && (
            <p className="text-red-500 text-xs mt-1">{errors.migrationName.message}</p>
          )}
        </div>

        {/* Error de conexión */}
        {testError && (
          <div className={`rounded-lg border p-4 ${testError.isWarning
            ? 'border-amber-200 bg-amber-50'
            : 'border-red-200 bg-red-50'
            }`}>
            <div className="flex">
              {testError.isWarning
                ? <AlertTriangle className="h-5 w-5 text-amber-500 mr-3 shrink-0 mt-0.5" />
                : <ServerOff className="h-5 w-5 text-red-600 mr-3 shrink-0" />
              }
              <div className="flex-1">
                <h4 className={`font-semibold ${testError.isWarning ? 'text-amber-900' : 'text-red-900'}`}>
                  {testError.title}
                </h4>
                <p className={`text-sm mt-1 ${testError.isWarning ? 'text-amber-700' : 'text-red-700'}`}>
                  {testError.description}
                </p>
              </div>
            </div>

            {/* 🆕 Panel de diagnóstico expandible */}
            {testError.diagnostic && (
              <DiagnosticPanel diagnostic={testError.diagnostic} t={t} />
            )}
          </div>
        )}

        {/* Botón validar (antes de conexión exitosa) */}
        {!connectionSuccess && (
          <div className="flex justify-end pt-6">
            <button
              type="submit"
              disabled={isTesting}
              className="px-6 py-2 rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 flex items-center disabled:opacity-60"
            >
              {isTesting && <Loader2 className="animate-spin mr-2 w-4 h-4" />}
              {t('wizard.step_source.validate_btn')}
            </button>
          </div>
        )}

        {/* Panel de éxito: selección de tabla + botón continuar */}
        {connectionSuccess && !testError?.isWarning && (
          <div className="mt-8 p-6 bg-indigo-50 border border-indigo-100 rounded-2xl animate-in slide-in-from-bottom-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold flex items-center">
                <CheckCircle2 className="w-5 h-5 text-emerald-500 mr-2" />
                {t('wizard.step_source.confirmed')}
              </h3>
              {/* Botón para refrescar tablas (solo BD) */}
              {showDatabaseForm && (
                <button
                  type="submit"
                  disabled={isTesting}
                  className="text-xs font-bold text-indigo-600 hover:text-indigo-800 flex items-center transition-colors"
                >
                  {isTesting
                    ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    : <RefreshCw className="w-3 h-3 mr-1" />
                  }
                  {t('wizard.step_source.refresh_tables')}
                </button>
              )}
            </div>

            {/* Selector de tabla (BD) */}
            {showTableSelect && (
              <div>
                {console.log('🔴 DEBUG SELECT:', {
                  store_value: source?.config?.selectedTable,
                  state_value: selectedTable,
                  watch_value: watch('selectedTable'),  // Si usa RHF
                  form_values: getValues()
                })}
                {console.log('🟢 RENDER SELECT:', { selectedTable, availableTables: availableTables?.length, showTableSelect })}
                <label className="text-sm font-medium mb-2 block">
                  {t('wizard.step_source.select_table')}
                </label>
                <select
                  value={selectedTable}
                  // onChange={(e) => setSelectedTable(e.target.value)}
                  onChange={(e) => {
                    const newTable = e.target.value;
                    setSelectedTable(newTable);
                    if (newTable !== source?.config?.selectedTable) {
                      setSourceColumns([]);
                      setMapping([]);
                      const tgtType = (target?.type || '').toLowerCase();
                      if (['csv', 'excel', 'dbf', 'googlesheets'].includes(tgtType)) {
                        setTargetColumns([]);
                      }
                    }
                  }}
                  className="w-full px-4 py-2 rounded-xl border outline-none bg-white border-slate-300"
                >
                  <option value="" disabled>{t('wizard.step_source.choose_table')}</option>
                  {selectedTable && !availableTables.includes(selectedTable) && (
                    <option value={selectedTable}>{selectedTable}</option>
                  )}
                  {availableTables.map(tbl => <option key={tbl} value={tbl}>{tbl}</option>)}
                </select>
              </div>
            )}

            {/* Input de nombre de hoja (Google Sheets) ANTES Excel y googlesheets*/}
            {/* {showSheetInput && ( */}
            {selectedEngine === 'googlesheets' && (
              <div>
                <label className="text-sm font-medium mb-2 block">
                  {t('wizard.step_source.sheet_name')}
                </label>
                <input
                  type="text"
                  value={selectedTable}
                  onChange={(e) => setSelectedTable(e.target.value)}
                  className="w-full px-4 py-2 rounded-xl border outline-none bg-white border-indigo-200 focus:border-indigo-500"
                  placeholder={t('wizard.step_source.sheet_ph')}
                />
              </div>
            )}

            {/* 🆕 S5: Botón Data Profiling — visible cuando hay tabla seleccionada */}
            {connectionSuccess && selectedTable && (
              <div className="mt-4">
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const res = await apiClient.get('/client/top-values-enabled');
                      if (!res.data?.enabled) { setTopValuesInfo(true); return; }
                    } catch { /* si falla el check, permitir acceso */ }
                    setShowProfile(true);
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2 text-xs font-bold text-teal-700 bg-teal-50 border border-teal-200 rounded-xl hover:bg-teal-100 transition"
                >
                  <BarChart3 className="w-4 h-4" />
                  {t('profiling.analyze') || 'Analizar datos'}
                </button>
                <p className="text-[10px] text-slate-400 mt-1 ml-1">
                  {t('profiling.analyze_hint') || 'Radiografía estadística de la tabla antes de mapear'}
                </p>
              </div>
            )}

            {/* UI MULTI-TABLA ORIGEN (N:M) — solo para BD, no archivos planos ni SaaS */}
            {connectionSuccess && selectedTypeMotor !== 'FLAT' && selectedTypeMotor !== 'NONE' && (
              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <label className="text-sm font-medium mb-3 block text-slate-700">
                  {t('wizard.step_source.selected_tables') || 'Tablas origen seleccionadas'}
                </label>

                {/* Lista de tablas ya agregadas */}
                {sourceSelectedTables.length > 0 && (
                  <div className="space-y-2 mb-4">
                    {sourceSelectedTables.map((table, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between bg-white p-3 rounded-lg border border-slate-200 hover:border-blue-300"
                        draggable
                        onDragStart={(e) => e.dataTransfer.effectAllowed = 'move'}
                      >
                        <span className="font-medium text-slate-700">
                          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-indigo-600 text-white text-xs mr-2">
                            {idx + 1}
                          </span>
                          {table}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeSourceTable(idx)}
                          className="text-red-600 hover:text-red-800 text-sm font-medium"
                        >
                          {t('wizard.delete') || 'Eliminar'}
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Botón + Agregar tabla origen */}
                <button
                  type="button"
                  onClick={() => {
                    if (selectedTable && !sourceSelectedTables.includes(selectedTable)) {
                      addSourceTable(selectedTable);
                      setSelectedTable('');
                    }
                  }}
                  disabled={!selectedTable}
                  className="w-full px-4 py-2 rounded-lg border-2 border-dashed border-blue-400 text-blue-600 font-medium hover:bg-blue-100 disabled:opacity-50"
                >
                  + {t('wizard.add_source_table') || 'Agregar tabla origen'}
                </button>

                {/* Indicador si hay múltiples tablas */}
                {sourceSelectedTables.length > 1 && (
                  <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded text-sm text-green-700">
                    ✓ {t('wizard.multi_table_mode_info') || 'Modo multi-tabla activado'} ({sourceSelectedTables.length} {t('wizard.tables') || 'tablas'})
                  </div>
                )}
              </div>
            )}

            {/* Botón continuar */}
            <div className="flex justify-end mt-8 border-t border-indigo-200 pt-6">
              <button
                type="button"
                onClick={handleContinue}
                // disabled={isTesting || (!selectedTable && !['csv', 'dbf'].includes(selectedEngine))}
                disabled={isTesting || (
                  !selectedTable &&
                  sourceSelectedTables.length === 0 &&
                  !['csv', 'dbf'].includes(selectedEngine)
                )}
                className="px-6 py-2 rounded-lg text-white bg-indigo-600 disabled:opacity-50 flex items-center"
              >
                {isTesting && <Loader2 className="animate-spin mr-2 w-4 h-4" />}
                {t('wizard.step_source.continue_btn')}
                <ArrowRight className="ml-2 h-5 w-5 inline" />
              </button>
            </div>
          </div>
        )}
      </form>

      {/* OBS-09: TopValues disabled info modal */}
      <Modal
        isOpen={topValuesInfo}
        title={t('profiling.top_values_disabled_title') || 'Visualización de datos reales desactivada'}
        message={t('profiling.top_values_disabled_msg') || 'Por su protección, la visualización de datos reales (valores más frecuentes) está desactivada por defecto. Para activarla, vaya a Mi Cuenta > Plan y active la opción "Visualizar datos reales en perfilado".'}
        onAccept={() => setTopValuesInfo(false)}
        onClose={() => setTopValuesInfo(false)}
        showCancel={false}
      />

      {/* 🆕 S5: Data Profiling Modal */}
      <ProfileModal
        isOpen={showProfile}
        onClose={() => setShowProfile(false)}
        sourceConfig={{
          engine: selectedEngine,
          config: {
            host: watch('host'),
            port: watch('port'),
            user: watch('user'),
            password: watch('password'),
            database: watch('database'),
            selectedTable: selectedTable,
            warehouse: watch('warehouse'),
            schema: watch('schema'),
            useSsh: watch('useSsh'),
            ssh_host: watch('ssh_host'),
            ssh_port: watch('ssh_port'),
            ssh_user: watch('ssh_user'),
            ssh_pkey_content: watch('ssh_pkey_content'),
            ssh_remote_bind_host: watch('ssh_remote_bind_host'),
            connectionMethod: watch('connectionMethod'),
            networkType: watch('networkType'),
            ...(editId ? { _idConfigHead: editId, _side: 'source' } : {}),
          },
        }}
        tableName={selectedTable}
        onApplySuggestions={null}
      />
    </div>
  );
}